import React from 'react';

import {Stack, Typography, useMediaQuery, useTheme} from '@mui/material';
import {Box} from '@mui/system';

import {getNextPlayerNumber, getPlayerById} from 'canvas/Drawer/player-utils';
import {useCanvasStore} from 'canvas/zustand';
import {useTranslation} from 'hooks';
import {DraggablePlayer} from 'module/client/team/member';
import {memberInfo} from 'module/client/team/member/useInfo';
import {useApiQuery as useClientApiQuery} from 'module/client/useClientApi';
import {useLicenseManager} from 'module/user';
import {useLayoutStore} from 'store';

import PlayerBadge from './PlayerBadge';
import PlayerCircle from './PlayerCircle';
import PlayerShirt from './PlayerShirt';

const styles = {
    title: {
        color: 'white',
        marginBottom: '6px',
        fontSize: '14px',
        padding: '0 12px',
        textAlign: 'center',
    },
    separator: {
        height: '1px',
        width: '100%',
        background: 'rgba(255, 255, 255, 0.1)',
        marginTop: '16px',
        marginBottom: '16px',
    }
};

export const Player = ({type, team, enemyTeam, teamMembers, enemyTeamMembers}) => {
    const translate = useTranslation();
    const canvas = useCanvasStore(state => state.canvas);
    useCanvasStore(state => state.canvas?.size());
    const theme = useTheme();
    const {hasAccess} = useLicenseManager();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const lineupIsDragging = useLayoutStore((state) => state.lineupIsDragging);
    const {data: clients} = useClientApiQuery();

    const currentTeam = type === 'home' ? team : enemyTeam;
    const teamClient = clients?.find(client => client.id === currentTeam.client_id);
    const currentTeamMembers = type === 'home'? teamMembers : enemyTeamMembers;

    const nextNumber = getNextPlayerNumber(canvas, currentTeam.id) || 1;
    // Get default colors from client if not set in team prefs
    const getDefaultColors = () => {
        if (teamClient?.colors?.value1 && teamClient?.colors?.value2) {
            return {
                value1: teamClient.colors.value1,
                value2: teamClient.colors.value2,
                textColor: teamClient.colors.value2
            };
        }

        return {
            value1: type === 'home' ? 'black' : 'white',
            value2: type === 'home' ? 'white' : 'black',
            textColor: type === 'home' ? 'white' : 'black',
        };
    };

    const defaultColors = getDefaultColors();
    // fallback to selected color if not set in team prefs
    const colors = {
        value1: team?.prefs?.player?.color1 || defaultColors.value1, 
        value2: team?.prefs?.player?.color2 || defaultColors.value2, 
        textColor: team?.prefs?.player?.color2 || defaultColors.textColor, 
    };
    const nextPlayer = currentTeamMembers?.sort((a, b) => a.number - b.number)
        ?.find(member => !getPlayerById(canvas, member.id));
    const {name} = memberInfo(nextPlayer);
    
    const player = {
        id: nextPlayer?.id,
        team: currentTeam?.id,
        number: nextNumber,
        name: name,
        ...nextPlayer
    };
    
    return (
        <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                paddingTop: '16px',
                overflowX: lineupIsDragging ? 'visible' : 'hidden',
                overflowY: 'hidden',
            }}>
            <Typography sx={styles.title}>{translate('board.lineup.team.magnet')}:</Typography>
            <DraggablePlayer isFreePlacementPlayer type="playerCircle" player={player} team={currentTeam} colors={colors}>
                {!(isMobile && lineupIsDragging && lineupIsDragging !== 'playerCircle') ?
                    <PlayerCircle number={player?.number || nextNumber} colors={colors} />
                    : null}
            </DraggablePlayer>
            <Box sx={styles.separator}></Box>
            <Typography sx={styles.title}>{translate('board.lineup.team.shirt')}:</Typography>
            <DraggablePlayer isFreePlacementPlayer type="player" player={player} team={currentTeam} colors={colors}>
                {!(isMobile && lineupIsDragging && lineupIsDragging !== 'player') ?
                    <PlayerShirt number={player?.number || nextNumber} colors={colors} />
                    : null}
            </DraggablePlayer>
            {hasAccess('player-image').access && (<>
                <Box sx={styles.separator}></Box>
                <Typography sx={styles.title}>{translate('board.lineup.team.magnet_picture')}:</Typography>
                <DraggablePlayer isFreePlacementPlayer type="playerCircleImage" player={player} team={currentTeam} colors={colors}>
                    {!(isMobile && lineupIsDragging && lineupIsDragging !== 'playerCircleImage') ?
                        <PlayerBadge player={player} colors={colors} />
                        : null}
                </DraggablePlayer>
            </>)}
        </Stack>
    );
};

export default Player;
